import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Camera,
  ChevronDown,
  LogOut,
  Mail,
  Phone,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBranding } from '../lib/branding';
import { PatientHeader } from './PatientLayout';
import { Avatar } from '../components/Avatar';
import { shrinkImage } from '../components/ImageUpload';
import { RichTextView } from '../components/RichTextView';
import {
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Field,
  Input,
  Modal,
  Spinner,
} from '../components/ui';
import { formatDate } from '../lib/format';

interface ProfileResponse {
  externalCode: string | null;
  birthDate: string | null;
  heightCm: number | null;
  enrolledAt: string | null;
  user: { fullName: string | null; email: string | null; username: string };
  caseManager: { fullName: string | null } | null;
}

export function ProfilePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const branding = useBranding();
  const [leaving, setLeaving] = useState(false);
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const profile = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => api.get<ProfileResponse>('/me/profile'),
  });

  const account = useQuery({
    queryKey: ['account'],
    queryFn: () => api.get<{ avatarUrl: string | null }>('/account'),
  });

  // La foto se guarda al elegirla, sin botón de confirmar: es un solo dato y ya
  // se ve el resultado en la pantalla.
  const saveAvatar = useMutation({
    mutationFn: (avatarUrl: string | null) => api.put('/account', { avatarUrl }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['account'] }),
  });

  if (profile.isError) return <ErrorState error={profile.error} onRetry={() => profile.refetch()} />;
  if (profile.isLoading || !profile.data) return <Spinner />;

  const data = profile.data!;

  return (
    <div className="max-w-lg lg:max-w-3xl mx-auto">
      <PatientHeader title="Tu perfil" />

      <div className="px-5 py-5 space-y-5">
        <Card className="p-5">
          {/* La foto se cambia desde la foto misma, no desde un campo aparte.
              Es lo único que el paciente puede editar de su perfil: el
              diagnóstico, el tratamiento y el referente los administra el
              equipo, y dejárselos editar convertiría su ficha clínica en algo
              que no se sostiene ante una auditoría. */}
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <Avatar name={data.user.fullName} src={account.data?.avatarUrl} size={64} />
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void shrinkImage(file, 256).then((value) => saveAvatar.mutate(value));
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                title="Cambiar tu foto"
                disabled={saveAvatar.isPending}
                className="absolute -bottom-1 -right-1 size-8 grid place-items-center rounded-full bg-primary text-primary-fg border-2 border-surface-raised shadow-sm disabled:opacity-60"
              >
                <Camera className="size-4" />
              </button>
            </div>

            <div className="min-w-0">
              <p className="text-xl font-semibold leading-tight">{data.user.fullName}</p>
              <p className="text-sm text-fg-muted mt-0.5 break-all">
                {data.user.email ?? data.user.username}
              </p>
              {account.data?.avatarUrl && (
                <button
                  type="button"
                  onClick={() => saveAvatar.mutate(null)}
                  className="text-sm text-fg-muted hover:text-danger mt-1"
                >
                  Quitar la foto
                </button>
              )}
            </div>
          </div>

          <dl className="mt-5 space-y-3 text-sm">
            <Row label="Fecha de nacimiento" value={data.birthDate ? formatDate(data.birthDate) : null} />
            <Row label="Altura" value={data.heightCm ? `${data.heightCm} cm` : null} />
            <Row label="En seguimiento desde" value={data.enrolledAt ? formatDate(data.enrolledAt) : null} />
            <Row label="Tu referente" value={data.caseManager?.fullName ?? null} />
            <Row label="Código de paciente" value={data.externalCode} />
          </dl>
        </Card>

        <ProgramsCard />

        <Card className="p-5">
          <h2 className="font-semibold mb-3">Tu equipo de seguimiento</h2>
          <div className="space-y-2">
            {branding.branding?.supportPhone && (
              <a href={`tel:${branding.branding.supportPhone}`}>
                <Button variant="outline" fullWidth>
                  <Phone className="size-4" />
                  {branding.branding.supportPhone}
                </Button>
              </a>
            )}
            {branding.branding?.supportEmail && (
              <a href={`mailto:${branding.branding.supportEmail}`}>
                <Button variant="outline" fullWidth>
                  <Mail className="size-4" />
                  {branding.branding.supportEmail}
                </Button>
              </a>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="font-semibold mb-3">Seguridad y datos</h2>
          <div className="space-y-2">
            <Button variant="outline" fullWidth onClick={() => navigate(`/${slug}/cambiar-clave`)}>
              <ShieldCheck className="size-4" />
              Cambiar mi contraseña
            </Button>
            <Button variant="outline" fullWidth onClick={() => navigate(`/${slug}/app/reportar`)}>
              <AlertTriangle className="size-4" />
              Reportar un efecto del tratamiento
            </Button>
          </div>
        </Card>

        {/*
          Los dos legales, y con el formato con el que se escribieron.
          Los términos no se mostraban en ningún lado —se cargaban en la consola
          y no llegaban a ninguna pantalla— y la privacidad salía como texto
          plano, así que un texto con títulos y listas se leía como un párrafo
          corrido. Es lo que la persona aceptó: tiene que poder volver a leerlo.
        */}
        {(branding.branding?.legalTerms || branding.branding?.legalPrivacy) && (
          <Card className="p-5">
            <h2 className="font-semibold mb-1">Lo legal</h2>
            <p className="text-sm text-fg-muted mb-3">Lo que aceptaste al entrar.</p>

            <div className="divide-y divide-border">
              {branding.branding?.legalTerms && (
                <Legal titulo="Términos y condiciones" texto={branding.branding.legalTerms} />
              )}
              {branding.branding?.legalPrivacy && (
                <Legal titulo="Privacidad de tus datos" texto={branding.branding.legalPrivacy} />
              )}
            </div>
          </Card>
        )}

        <Button variant="ghost" fullWidth onClick={() => setLeaving(true)}>
          <LogOut className="size-4" />
          Cerrar sesión
        </Button>

        <p className="text-center text-xs text-fg-subtle pb-4">
          {branding.programName} · funciona con Trellis
        </p>

        {/* Un paciente que cierra sesión sin querer y no recuerda la contraseña
            que le generó la institución queda afuera de su programa hasta que
            alguien se la restablezca. */}
        <ConfirmDialog
          open={leaving}
          title="Cerrar tu sesión"
          description="Para volver a entrar vas a necesitar tu usuario y tu contraseña."
          confirmLabel="Cerrar sesión"
          onCancel={() => setLeaving(false)}
          onConfirm={async () => {
            await logout();
            navigate(`/${slug}`, { replace: true });
          }}
        />
      </div>
    </div>
  );
}

interface MisProgramas {
  programs: Array<{
    enrollmentId: string;
    status: 'ACTIVE' | 'ENDED';
    enrolledAt: string;
    endedAt: string | null;
    name: string;
    slug: string;
    programSlug: string;
    institución: string;
    form: string | null;
  }>;
}

/**
 * Sus programas: sumar uno y darse de baja.
 *
 * Las dos acciones son suyas y de nadie más. La consola de la institución no
 * puede anotarla ni sacarla: entrar y salir de un programa de salud es una
 * decisión de la persona, y hacerlo desde una consola la movía sin que se
 * enterara.
 */
function ProgramsCard() {
  const queryClient = useQueryClient();
  const [sumando, setSumando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bajando, setBajando] = useState<MisProgramas['programs'][number] | null>(null);

  const mios = useQuery({
    queryKey: ['me', 'programas'],
    queryFn: () => api.get<MisProgramas>('/me/programas'),
  });

  const refrescar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['me', 'programas'] });
    // La app de adentro también cambia: uno menos es una sección menos.
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const sumar = useMutation({
    mutationFn: () => api.post('/me/programas', { code: codigo.trim() }),
    onSuccess: () => {
      setSumando(false);
      setCodigo('');
      setError(null);
      refrescar();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'No pudimos sumarte a ese programa'),
  });

  const darDeBaja = useMutation({
    mutationFn: () => api.post(`/me/programas/${bajando!.enrollmentId}/baja`, {}),
    onSuccess: () => {
      setBajando(null);
      refrescar();
    },
  });

  const activos = (mios.data?.programs ?? []).filter((entry) => entry.status === 'ACTIVE');
  const anteriores = (mios.data?.programs ?? []).filter((entry) => entry.status === 'ENDED');

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-semibold">Tus programas</h2>
        <Button size="sm" variant="outline" onClick={() => setSumando(true)}>
          <Plus className="size-4" />
          Sumar uno
        </Button>
      </div>

      {mios.isLoading ? (
        <Spinner label="" />
      ) : activos.length === 0 ? (
        <p className="text-sm text-fg-muted">
          No estás en ningún programa. Si tenés un código, sumalo acá.
        </p>
      ) : (
        <ul className="space-y-2">
          {activos.map((programa) => (
            <li
              key={programa.enrollmentId}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
            >
              <div className="min-w-0 grow">
                <p className="font-medium truncate">{programa.name}</p>
                <p className="text-sm text-fg-muted truncate">
                  {[programa.institución, programa.form, `desde ${formatDate(programa.enrolledAt)}`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setBajando(programa)}>
                Darme de baja
              </Button>
            </li>
          ))}
        </ul>
      )}

      {anteriores.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-sm font-medium mb-1.5">Programas anteriores</p>
          <ul className="space-y-1 text-sm text-fg-muted">
            {anteriores.map((programa) => (
              <li key={programa.enrollmentId}>
                {programa.name}
                {programa.endedAt && ` · hasta ${formatDate(programa.endedAt)}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal
        open={sumando}
        onClose={() => {
          setSumando(false);
          setError(null);
        }}
        title="Sumar un programa"
        footer={
          <>
            <Button variant="outline" onClick={() => setSumando(false)}>
              Cancelar
            </Button>
            <Button
              loading={sumar.isPending}
              disabled={codigo.trim().length < 4}
              onClick={() => {
                setError(null);
                sumar.mutate();
              }}
            >
              Sumar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-[15px] text-fg-muted">
            Escribí el código de seis caracteres que figura en el material que te dieron. No hace
            falta que sea del misma institución: entrás con la misma cuenta.
          </p>
          <Field label="Código del programa" htmlFor="sumar-codigo" error={error ?? undefined}>
            <Input
              id="sumar-codigo"
              autoComplete="off"
              autoCapitalize="characters"
              className="font-mono text-lg tracking-[0.2em] uppercase"
              value={codigo}
              onChange={(event) => setCodigo(event.target.value.toUpperCase())}
            />
          </Field>
        </div>
      </Modal>

      {/* Lo que se pierde y lo que se conserva, antes de decidir: es la
          convención del proyecto para todo lo que no tiene vuelta atrás. */}
      <ConfirmDialog
        open={bajando !== null}
        title={`Darte de baja de ${bajando?.name ?? ''}`}
        description="Dejás de recibir sus recordatorios y de ver su calendario. Lo que registraste se conserva: es parte de tu historia clínica y la institución tiene que guardarlo. Tu cuenta sigue igual, y si estás en otro programa seguís entrando. Para volver vas a necesitar el código otra vez."
        confirmLabel="Darme de baja"
        onCancel={() => setBajando(null)}
        onConfirm={() => darDeBaja.mutate()}
      />
    </Card>
  );
}

/** Un legal, plegado. Abierto de entrada empuja todo lo demás fuera de la vista. */
function Legal({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <details className="py-2 group">
      <summary className="cursor-pointer font-medium text-[15px] list-none flex items-center justify-between gap-3">
        {titulo}
        <ChevronDown className="size-4 text-fg-subtle transition-transform group-open:rotate-180" />
      </summary>
      <RichTextView source={texto} className="text-sm text-fg-muted mt-2 leading-relaxed" />
    </details>
  );
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="font-medium text-right">{value}</dd>
    </div>
  );
}
